import { useState, useRef, useEffect, useMemo } from "react";
// import { useGetTitle } from "../../../hooks/useGetTitle";
import { useNavigate, useParams } from "react-router-dom";
// import { toast } from "react-toastify";

import { 
  Alert, Button, Box,
  Chip,
  Collapse,
  Divider,
  FormControl, FormControlLabel,
  IconButton, InputAdornment,
  LinearProgress,
  OutlinedInput,
  Paper,
  Stack,
  Switch,
  Typography, TextField,
  useTheme,
} from "@mui/material";

//import styles from "../../main/main.module.css";
import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search"; // MUI의 검색 아이콘
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import VerifiedIcon from "@mui/icons-material/Verified";

import dayjs from "dayjs";
import { axiosPrivate as axiosInstance } from "../../../../../api/axios";

import { 
  useGetTableData, 
  usePutTableData, 
  useDeleteTableData 
} from "../../../components/Table/hooks";

import { 
  assessmentResultColumns, assessmentResultColumns_admin 
} from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import Buttons from "../../../../common/Buttons";
import { EmptyState } from "../../../components/common/EmptyState";
import { 
  BreadcrumbsTable,
  ConfirmDialog, ConfirmDialogSingle, 
  deepCopy,
  SnackbarAlert, 
  formatDateWithDayjs, formatDateToDayjs, getTodayByDayjs,
} from "../../../components/common/HelperFunc";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 12px;
    overflow: auto;
  }
`;

//////////////////////////////////
// 위험성평가 승인 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "평가관리";
const CHILD_TITLE = "위험성평가 승인";

const RESULT_FILTERS = [
  { key: "all", label: "전체" },
  { key: "pending", label: "승인 대기" },
  { key: "approved", label: "승인 완료" },
  { key: "improve", label: "개선 필요" },
  { key: "planned", label: "대책 등록" },
];

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const isApprovedEval = (row) => toNumber(row?.eval_status) >= 20;
const needsImprovePlan = (row) => toNumber(row?.requires_improve) === 1;
const hasImprovePlan = (row) => Boolean(row?.improve_id || row?.improve_plan);
const isImproveDone = (row) => toNumber(row?.improve_status) >= 20;
const getRiskScore = (row) => toNumber(row?.frequency_level) * toNumber(row?.importancy_level);
const getEvalDateParam = () => dayjs().format("YYYY-MM-DD");

export function AssessmentResult() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 위험성 수준 판단 기준 가능성(빈도) 데이터 조회 요청
  const getFrequencyData = useGetTableData(
    "request",
    "/getSafeCalFrequency",
    group_code,
    project_code
  );
  // 위험성 수준 판단 기준 중대성(강도) 데이터 조회 요청
  const getImportancyData = useGetTableData(
    "request",
    "/getSafeCalImportancy",
    group_code,
    project_code
  );
  // 위험성 허용 기준 데이터 조회 요청
  const getStandardData = useGetTableData(
    "request",
    "/getSafeCalStandard",
    group_code,
    project_code
  );

  // 데이터 조회 요청
  const getData = useGetTableData(
    "request",
    "/getRiskAssessmentApproval",
    group_code,
    project_code
  );
  const [progressSignals, setProgressSignals] = useState({
    pendingHazards: [],
    approvedProposals: [],
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!group_code || !project_code) return undefined;

    let ignore = false;
    setProgressSignals((prev) => ({ ...prev, isLoading: true, isError: false }));

    Promise.all([
      axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/pending-hazards`, {
        params: { eval_date: getEvalDateParam() },
      }),
      axiosInstance.get(`/risk/${group_code}/${project_code}/worker-hazard-proposals`, {
        params: { status: "approved" },
      }),
    ])
      .then(([pendingResponse, proposalResponse]) => {
        if (ignore) return;
        setProgressSignals({
          pendingHazards: pendingResponse.data?.rows || [],
          approvedProposals: proposalResponse.data || [],
          isLoading: false,
          isError: false,
        });
      })
      .catch(() => {
        if (ignore) return;
        setProgressSignals((prev) => ({ ...prev, isLoading: false, isError: true }));
      });

    return () => {
      ignore = true;
    };
  }, [group_code, project_code]);
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putRiskAssessment",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteRiskAssessment",
    group_code,
    project_code
  );

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
    isOpen: false,
    title: '',
    message: '',
  });
  const openMessageAlarm = (title='', message='',) => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeMessageAlarm = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: '',
      message: '',
    });
  };

  // 임시 데이터 상태 관리
  const [tempData, setTempData] = useState([]);
  const handleSetTempData = (data) => setTempData(data);
  useEffect(() => {
    const tempData = deepCopy(getData.data);
    for(let i = 0; i < tempData.length; ++i) {
      // 인덱스 추가(행 편집 용도로 사용)
      tempData[i].__index = i;
      // 평가일자 포맷 변환(관리자모드 시 편집 용도로 사용)
      tempData[i].__eval_date = formatDateToDayjs(tempData[i].eval_date);
      // 승인일자 포맷 변환(관리자모드 시 편집 용도로 사용)
      tempData[i].__approve_date = formatDateToDayjs(tempData[i].approve_date);
    }
    setTempData(tempData);
  },[getData.data]);

  const [resultFilter, setResultFilter] = useState("all");
  const resultSummary = useMemo(() => {
    const rows = tempData || [];
    const pendingCount = rows.filter((row) => !isApprovedEval(row)).length;
    const approvedCount = rows.filter(isApprovedEval).length;
    const improveRequiredCount = rows.filter(needsImprovePlan).length;
    const plannedCount = rows.filter((row) => needsImprovePlan(row) && hasImprovePlan(row)).length;
    const doneCount = rows.filter((row) => needsImprovePlan(row) && isImproveDone(row)).length;
    const avgRisk = rows.length
      ? Math.round((rows.reduce((sum, row) => sum + getRiskScore(row), 0) / rows.length) * 10) / 10
      : 0;

    return {
      total: rows.length,
      pendingCount,
      approvedCount,
      improveRequiredCount,
      plannedCount,
      doneCount,
      avgRisk,
    };
  }, [tempData]);

  const workflowSummary = useMemo(() => {
    const pendingHazards = progressSignals.pendingHazards || [];
    const approvedProposals = progressSignals.approvedProposals || [];
    const approvedProposalDangerIds = new Set(
      approvedProposals
        .map((proposal) => Number(proposal.approved_danger_cause_id))
        .filter(Boolean)
    );
    const workerEvaluatedCount = (tempData || []).filter((row) =>
      approvedProposalDangerIds.has(Number(row.danger_cause_id))
    ).length;
    const workerPendingCount = pendingHazards.filter((row) =>
      approvedProposalDangerIds.has(Number(row.danger_cause_id))
    ).length;
    const trackedTotal = resultSummary.total + pendingHazards.length;
    const evaluatedRate = trackedTotal
      ? Math.round((resultSummary.total / trackedTotal) * 100)
      : 0;
    const approvalRate = resultSummary.total
      ? Math.round((resultSummary.approvedCount / resultSummary.total) * 100)
      : 0;

    return {
      pendingHazardCount: pendingHazards.length,
      trackedTotal,
      evaluatedRate,
      approvalRate,
      workerProposalCount: approvedProposals.length,
      workerEvaluatedCount,
      workerPendingCount,
    };
  }, [progressSignals.approvedProposals, progressSignals.pendingHazards, resultSummary, tempData]);

  const filteredTempData = useMemo(() => {
    const rows = tempData || [];
    if (resultFilter === "pending") return rows.filter((row) => !isApprovedEval(row));
    if (resultFilter === "approved") return rows.filter(isApprovedEval);
    if (resultFilter === "improve") return rows.filter(needsImprovePlan);
    if (resultFilter === "planned") return rows.filter((row) => needsImprovePlan(row) && hasImprovePlan(row));
    return rows;
  }, [tempData, resultFilter]);

  const pendingRows = useMemo(
    () => (tempData || []).filter((row) => !isApprovedEval(row)),
    [tempData]
  );
  const isProcessing = Boolean(putData.isPending || putData.isLoading || deleteData.isPending || deleteData.isLoading);

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>

      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
        
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setGlobalFilter('');
                        table.setGlobalFilter('');
                        setShowSearch(false); // 닫기
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  ),
                }
              }}
            />
          </Collapse>
        </Box>
          {/* 삭제버튼 */}
          {showAdminMode && 
            <Buttons.Delete 
              onClick={openDeleteModal} // 행 삭제 로직 실행
              disabled={table.getSelectedRowModel().rows.length === 0 || isProcessing}
              sx={{ padding: '12px 16px' }}
            >
              데이터 삭제
            </Buttons.Delete>
          }
          {/* 관리자 모드 */}
          <FormControl component="fieldset">
            <FormControlLabel
              value="top"
              control={
                <Switch color="primary" 
                  checked={showAdminMode}
                  onChange={(event) => {
                    setShowAdminMode(event.target.checked);
                    clearSelection();
                  }}
                />
              }
              label="관리자모드"
              labelPlacement="end" // bottom,end,start,top
              sx={{marginLeft : '4px'}}
            />
          </FormControl>
      </Box>
    </Box>
  );

  // 행 편집 작업 (가능성빈도, 중대성강도)
  const changeRowData = ({ values, row, table }) => {
    //console.log("✅ 편집 호출 완료!", values);
    //console.log("✅ 편집 호출 완료!", row.original);

    // 편집된 데이터 변경
    const findData = tempData.find(item => {
      return item.__index === row.original.__index;
    });
    Object.keys(values).forEach((key) => {
      if (findData[key] !== values[key]) {
        findData[key] = values[key];
      }
    });

    // 데이터 변환
    const frequencyId = getFrequencyId(findData.frequency_level);
    if(frequencyId) {
      findData.frequency_id = frequencyId;
    }
    const importancyId = getImportancyId(findData.importancy_level);
    if(importancyId) {
      findData.importancy_id = importancyId;
    }
    const calculateLevel = calculateRiskLevel(findData.frequency_level, findData.importancy_level);
    const standardId = getStandardId(calculateLevel);
    if(standardId) {
      findData.standard_id = standardId;
    }

    // TempData 변경
    const data = tempData.map(item => {
      return item.__index === row.original.__index ? findData : item;
    });
    handleSetTempData(data);
    
    //table.setEditingRow(null); // 에디터 모드 종료
  };
  // 위험성 평가 승인 테이블의 전체 행 데이터 편집 완료 액션
  const completeEditAllConfirm = !showAdminMode ? (
          <Buttons.Custom
            startIcon={<CheckIcon />}
            disabled={isProcessing}
            onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        
        const findDatas = tempData.filter((item) => {
          return item.eval_status < 20; // 20: 승인 완료
        });
        
        if(findDatas.length <= 0) {
          openMessageAlarm(
            '안내',
            '전체 승인할 항목이 없습니다.'
          );
          return;
        }
        updateEditData(findDatas);
      }}
      color="error"
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 승인
    </Buttons.Custom> 
  ) : (
          <Buttons.Custom
            startIcon={<CheckIcon />}
            disabled={isProcessing}
            onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        const findDatas = tempData.filter((item) => {
          // 승인 완료 이후 항목만 수정 가능
          const isWaiting = item?.eval_status >= 20 ? true : false;
          // 평가일자 및 승인일자가 유효한지 확인
          const isValidDate = dayjs(item.__eval_date).isValid() && dayjs(item.__approve_date).isValid();
          return isWaiting && isValidDate;
        });
        if(findDatas.length <= 0) {
          openMessageAlarm(
            '안내',
            '전체 수정할 항목이 없습니다.'
          );
          return;
        }

        updateEditDataByAdmin(findDatas);
      }}
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 수정
    </Buttons.Custom> 
  );
  // 위험성 평가 승인 테이블의 개별 행 데이터 편집 완료 액션
  const completeEditConfirm = (rowData) => {
    return(
      <>
        {!showAdminMode ? (
          <Buttons.Custom
            //startIcon={<CheckIcon />}
            disabled={rowData.eval_status >= 20 || isProcessing} // 이미 승인된 항목은 비활성화
            onClick={(event) =>{
              event.stopPropagation(); // 이벤트 전파 중단
              updateEditData(rowData);
            }}
            color="error"
            sx={{ padding: '8px 12px' }}
          >
            승인
          </Buttons.Custom>
        ) : (
          <Buttons.Custom
            //startIcon={<CheckIcon />}
            disabled={rowData?.eval_status < 20 || isProcessing} // 승인 이전에는 수정 불가
            onClick={(event) =>{
              event.stopPropagation(); // 이벤트 전파 중단
              if(checkDataByAdmin(rowData)) {
                openMessageAlarm(
                  '안내',
                  '평가일자 및 승인일자를 설정해주세요.'
                );
                return;
              }
              updateEditDataByAdmin(rowData);
            }}
            sx={{ padding: '8px 12px' }}
          >
            수정
          </Buttons.Custom>
        )}
        {needsImprovePlan(rowData) && (
          <Buttons.Custom
            startIcon={<BuildCircleIcon />}
            disabled={!isApprovedEval(rowData) || isProcessing}
            onClick={(event) => {
              event.stopPropagation();
              goStrategyRegister(rowData);
            }}
            sx={{ padding: '8px 10px' }}
          >
            대책
          </Buttons.Custom>
        )}
      </>
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 

      const updateData = [];
      for(const item of tempRowDatas) {
        const data = {
          eval_id: item.eval_id,
          frequency_id: item.frequency_id,
          importancy_id: item.importancy_id,
          standard_id: item.standard_id,
          eval_status: 20, // 평가 상태: 20(승인 완료)
          approve_date: getTodayByDayjs(),
        };
        updateData.push(data);
      }
      // 데이터베이스 업데이트
      await putData.mutateAsync({ updateData });
      await getData.refetch?.();
      clearSelection();
      
      openDataAlert(`${tempRowDatas.length}건 승인 처리가 완료되었습니다. 개선 필요 항목은 개선대책 관리에서 이어서 처리할 수 있습니다.`);
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
      openDataAlert("승인 처리 중 오류가 발생했습니다.", "error");
    }
  }
  // DB 업데이트 (수정 데이터) - 관리자 모드
  const updateEditDataByAdmin = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 
      const updateData = [];
      for(const item of tempRowDatas) {
        // 평가일자가 변경되었는지 확인 후 반영
        const __evalDate = formatDateWithDayjs(item.__eval_date);
        const evalDate = formatDateWithDayjs(item.eval_date);
        const isSameEval = dayjs(__evalDate).isSame(dayjs(evalDate), 'day');

        const __approve_date = formatDateWithDayjs(item.__approve_date);
        const approveDate = formatDateWithDayjs(item.approve_date);
        const isSameApprove = dayjs(__approve_date).isSame(dayjs(approveDate), 'day');

        let updateDate = approveDate;
        if(!isSameEval) { updateDate = __evalDate; } // 평가일자가 변경된 경우 우선 반영
        if(!isSameApprove) { updateDate = __approve_date; } // 승인일자가 변경된 경우 최우선 반영

        const data = {
          eval_id: item.eval_id,
          frequency_id: item.frequency_id,
          importancy_id: item.importancy_id,
          standard_id: item.standard_id,
          eval_status: 20, // 평가 상태: 20(승인 완료)
          eval_date: isSameEval ? evalDate : __evalDate,
          approve_date: isSameApprove ? approveDate : __approve_date,
          update_date: updateDate,
        };
        updateData.push(data);
      }
      // 데이터베이스 업데이트
      await putData.mutateAsync({ updateData });
      await getData.refetch?.();
      clearSelection();
      
      openDataAlert(`${tempRowDatas.length}건 수정 내용이 저장되었습니다.`);
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
      openDataAlert("수정 내용 저장 중 오류가 발생했습니다.", "error");
    }
  }

  // 위험성 평가 승인 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });

  // DB 업데이트 (삭제 데이터)
  const deleteEditData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    // 여기에 실제 동작 실행 코드
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = selectedRows.map((row) => row.eval_id);

      // if(checkDeleteConfirm(selectedIds)) {
      //     handleMessageConfirmAction(
      //         '안내',
      //         `평가 상태가 '평가완료' 또는 '승인완료'인 데이터만 삭제할 수 있습니다.`
      //     );
      //     return;
      // }

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: 'eval_id',
        param: selectedIds,
      });

      if(result.status === 200) {
        openDataAlert(`${selectedIds.length}건 삭제가 완료되었습니다.`);
        await getData.refetch?.();
      }

      // 선택 상태 초기화
      clearSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
      openDataAlert("삭제 처리 중 오류가 발생했습니다.", "error");
    }
  };

  ////////////////////////
  // Helper Functions
  ////////////////////////

  // 가능성(빈도) 레벨에 따른 ID 반환
  const getFrequencyId = (freqLevel) => {
    if(freqLevel === null) {
      return freqLevel;
    }
          
    const data = getFrequencyData.data.find( item => {
      return freqLevel === item.frequency_level;
    });
    return data.frequency_id;
  }

  // 중대성(강도) 레벨에 따른 ID 반환
  const getImportancyId = (impLevel) => {
    if(impLevel === null) {
      return impLevel;
    }

    const data = getImportancyData.data.find( item => {
      return impLevel === item.importancy_level;
    });
    return data.importancy_id;
  }

  // 위험도 계산 함수
  const calculateRiskLevel = (freqLevel, impLevel) => {
    const riskValue = freqLevel * impLevel;
    return riskValue;
  };

  // 위험성 허용 기준 ID 반환
  const getStandardId = (riskLevel) => {
    if(riskLevel <= 0) {
      return null;
    } 

    const data = getStandardData.data.find( item => {
      return riskLevel <= item.risk_level_max;
    });
    return data.standard_id;
  };

  // 데이터 빈칸 체크 - 관리자 모드
  const checkDataByAdmin = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    for(const item of tempData) {
      const evalDate = dayjs(item.__eval_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
      const approveDate = dayjs(item.__approve_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
      if(!evalDate || !approveDate) {
        return true;
      }
    }
    return false;
  };

  const approvePendingRows = () => {
    if(pendingRows.length <= 0) {
      openMessageAlarm("안내", "승인 대기 중인 평가가 없습니다.");
      return;
    }
    updateEditData(pendingRows);
  };

  const goStrategyRegister = (rowData = null) => {
    const params = new URLSearchParams();
    const focusDate = rowData?.approve_date || rowData?.eval_date;

    if (rowData?.eval_id) params.set("eval_id", rowData.eval_id);
    if (rowData?.improve_id) params.set("improve_id", rowData.improve_id);
    if (rowData?.register_cause_id) params.set("register_cause_id", rowData.register_cause_id);
    if (rowData?.machine_id) params.set("machine_id", rowData.machine_id);
    if (focusDate && dayjs(focusDate).isValid()) {
      params.set("year", dayjs(focusDate).year());
      params.set("month", dayjs(focusDate).month() + 1);
    }
    if (rowData) params.set("source", "assessment-result");

    const queryString = params.toString();
    navigate(
      `/safe/${group_code}/${project_code}/strategy/register${queryString ? `?${queryString}` : ""}`,
      rowData ? { state: { focusImprove: rowData } } : undefined
    );
  };

  const summaryItems = [
    {
      label: "현재 주기 미평가",
      value: progressSignals.isLoading ? "-" : workflowSummary.pendingHazardCount,
      icon: <PlaylistAddCheckIcon />,
      tone: "#f97316",
    },
    {
      label: "전체 평가",
      value: resultSummary.total,
      icon: <FactCheckIcon />,
      tone: "#2563eb",
    },
    {
      label: "승인 대기",
      value: resultSummary.pendingCount,
      icon: <PendingActionsIcon />,
      tone: "#f97316",
    },
    {
      label: "승인 완료",
      value: resultSummary.approvedCount,
      icon: <VerifiedIcon />,
      tone: "#16a34a",
    },
    {
      label: "개선 필요",
      value: resultSummary.improveRequiredCount,
      icon: <BuildCircleIcon />,
      tone: "#dc2626",
    },
    {
      label: "대책 등록",
      value: resultSummary.plannedCount,
      icon: <AssignmentTurnedInIcon />,
      tone: "#7c3aed",
    },
  ];

  const renderSummaryPanel = () => (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={resultSummary.pendingCount > 0 ? "warning" : "success"}
              label={resultSummary.pendingCount > 0 ? "승인 대기 있음" : "승인 완료"}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary" }}>
              {dayjs().month() + 1}월 위험성평가 결과
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
            승인 후 개선 필요 항목은 개선대책 관리 화면에서 바로 이어서 처리합니다.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
          <Button
            variant="outlined"
            startIcon={<BuildCircleIcon />}
            onClick={() => goStrategyRegister()}
            sx={{ borderRadius: 1.5, fontWeight: 700, whiteSpace: "nowrap" }}
          >
            개선대책 관리
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={approvePendingRows}
            disabled={pendingRows.length === 0 || isProcessing}
            sx={{ borderRadius: 1.5, fontWeight: 800, whiteSpace: "nowrap" }}
          >
            대기 {pendingRows.length}건 승인
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(6, minmax(0, 1fr))",
          },
        }}
      >
        {summaryItems.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              minWidth: 0,
              p: 2,
              borderRight: {
                md: index === summaryItems.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
              },
              borderBottom: {
                xs: index < 4 ? `1px solid ${theme.palette.divider}` : "none",
                md: "none",
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "grid",
                  placeItems: "center",
                  color: item.tone,
                  bgcolor: `${item.tone}14`,
                  flex: "0 0 auto",
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: 24, color: "text.primary", fontWeight: 850, lineHeight: 1.1 }}>
                  {item.value}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" color="primary" icon={<FactCheckIcon />} label="진행 현황" />
                <Typography sx={{ fontWeight: 900 }}>
                  미평가 → 평가 완료 → 승인 완료 → 개선 추적
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                현재 평가 주기 기준으로 작성률과 승인률을 함께 추적합니다.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<PersonSearchIcon />}
                label={`근로자 제안 출신 ${workflowSummary.workerProposalCount}건`}
              />
              <Chip
                size="small"
                color={workflowSummary.workerPendingCount > 0 ? "warning" : "success"}
                variant="outlined"
                label={`제안 출신 미평가 ${workflowSummary.workerPendingCount}건`}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  평가 작성률
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {workflowSummary.evaluatedRate}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={workflowSummary.evaluatedRate}
                sx={{ height: 9, borderRadius: 99, bgcolor: "#e2e8f0" }}
              />
              <Typography variant="caption" color="text.secondary">
                전체 {workflowSummary.trackedTotal}건 중 평가 완료 {resultSummary.total}건
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  승인 완료율
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {workflowSummary.approvalRate}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                color="success"
                value={workflowSummary.approvalRate}
                sx={{ height: 9, borderRadius: 99, bgcolor: "#e2e8f0" }}
              />
              <Typography variant="caption" color="text.secondary">
                평가 완료 {resultSummary.total}건 중 승인 완료 {resultSummary.approvedCount}건
              </Typography>
            </Box>
          </Box>

          {progressSignals.isError ? (
            <Alert severity="warning" sx={{ py: 0.75 }}>
              미평가/근로자 제안 출처 정보를 일부 불러오지 못했습니다. 평가 결과 데이터는 정상 표시됩니다.
            </Alert>
          ) : null}
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          p: 1.5,
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {RESULT_FILTERS.map((filter) => (
            <Chip
              key={filter.key}
              label={filter.label}
              variant={resultFilter === filter.key ? "filled" : "outlined"}
              color={resultFilter === filter.key ? "primary" : "default"}
              onClick={() => setResultFilter(filter.key)}
              sx={{ fontWeight: 700 }}
            />
          ))}
        </Stack>
        <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>
          평균 위험도 {resultSummary.avgRisk}
        </Typography>
      </Box>

      {resultSummary.improveRequiredCount > 0 && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          개선 필요 항목 {resultSummary.improveRequiredCount}건 중 {resultSummary.plannedCount}건이 대책과 연결되어 있습니다.
        </Alert>
      )}
    </Paper>
  );

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {renderSummaryPanel()}
        {/** 위험성 평가 승인 테이블 */}
        {!getData.isLoading && !filteredTempData.length ? (
          <EmptyState
            icon={<FactCheckIcon />}
            title={resultFilter === "all" ? "표시할 평가 결과가 없습니다" : "조건에 맞는 평가 결과가 없습니다"}
            description={
              resultFilter === "all"
                ? "위험성평가 Wizard에서 평가를 제출하면 승인 대기 항목이 이 화면에 표시됩니다."
                : "상단 필터를 전체로 변경하거나 Wizard에서 새 평가를 제출해보세요."
            }
            actionLabel="개선대책 관리로 이동"
            actionIcon={<BuildCircleIcon />}
            onAction={() => goStrategyRegister()}
          />
        ) : (
          <NewTableTemplate
            ref={tableRef}
            key={showAdminMode ? 'admin-table' : 'normal-table'}   // key로 인스턴스 분리
            columns={showAdminMode ? assessmentResultColumns_admin : assessmentResultColumns}
            data={filteredTempData || []}
            topToolbarContent={customTableTopToolbar}
            renderRowActionHeader={completeEditAllConfirm}
            renderRowActionContent={completeEditConfirm}
            renderRowActionCustomOptions={changeRowData}
            editDisplayMode={"row"}
            enableCustomGlobalFilter={true}
            enabledSelectionRow={showAdminMode}
            enabledSelectionAllRow={showAdminMode}
            templateSX ={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "0.375rem", 
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
            }}
          />
        )}
        {/* 삭제 메시지 */}
        <ConfirmDialog  
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteEditData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert 
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        {/* 기타 메시지 박스 알람 */}
        <ConfirmDialogSingle  
          open={messageAlarm.isOpen}
          title={messageAlarm.title}
          message={messageAlarm.message}
          onCancel={closeMessageAlarm}
        />
      </TableContainer>
    );
  }
    
  // AssessmentResult 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}
